import type { AvlItem } from '@/services/approvedVendorList';
import isEqual from 'lodash/isEqual';

import { AVL_BULK_EDIT_EXCLUDED_FIELDS } from 'types';

import { baseFields, equipmentFields, FieldKind, FieldSpec } from './avlFieldRegistry';

export interface SharedFieldSpec {
  spec: FieldSpec;
  values: unknown[];
}

/** Fixed, selection-independent field order: baseFields, then each equipmentType's fields, deduped by name. */
const registryFieldOrder: FieldSpec[] = (() => {
  const seen = new Set<string>();
  const ordered: FieldSpec[] = [];
  for (const spec of [...baseFields, ...Object.values(equipmentFields).flat()]) {
    if (!seen.has(spec.name)) {
      seen.add(spec.name);
      ordered.push(spec);
    }
  }
  return ordered;
})();

const excludedFieldNames: ReadonlySet<string> = new Set(AVL_BULK_EDIT_EXCLUDED_FIELDS);

const compareAsStrings = (a: unknown, b: unknown): number => String(a).localeCompare(String(b));

/** Order shouldn't make two multiselect sets distinct. */
const isMultiselectSetEqual = (a: unknown[], b: unknown[]): boolean =>
  a.length === b.length && isEqual(a.toSorted(compareAsStrings), b.toSorted(compareAsStrings));

const areValuesEqual = (a: unknown, b: unknown, kind: FieldKind): boolean =>
  kind === 'multiselect' && Array.isArray(a) && Array.isArray(b) ? isMultiselectSetEqual(a, b) : isEqual(a, b);

const dedupeValues = (rawValues: unknown[], kind: FieldKind): unknown[] => {
  const deduped: unknown[] = [];
  for (const value of rawValues) {
    if (!deduped.some((existing) => areValuesEqual(existing, value, kind))) deduped.push(value);
  }
  return deduped;
};

/** Shared fields take their position from the first type that declares them, which can leave another type's section empty. */
const dropEmptySections = (specs: FieldSpec[]): FieldSpec[] =>
  specs.filter((spec, index) => {
    if (spec.kind !== 'section') return true;
    const nextSectionIndex = specs.findIndex((other, i) => i > index && other.kind === 'section');
    const end = nextSectionIndex === -1 ? specs.length : nextSectionIndex;
    return specs.slice(index + 1, end).some((other) => other.kind !== 'section');
  });

/**
 * Computes the fields eligible for bulk edit across a selection of AVL items: the intersection of
 * every selected item's equipmentType field registry (baseFields + that type's equipmentFields),
 * minus the shared excluded-field set (identity, series-info, allocation). For each eligible
 * field, returns the deduped set of values across the selection — a single value means the
 * selection agrees, more than one means it's mixed.
 */
export const computeSharedFieldSpecs = (items: AvlItem[]): SharedFieldSpec[] => {
  if (items.length === 0) return [];

  const presentTypes = Array.from(new Set(items.map((item) => item.equipmentType)));
  const nameSetsByType = presentTypes.map(
    (type) => new Set([...baseFields, ...equipmentFields[type]].map((spec) => spec.name)),
  );

  const eligibleFields = dropEmptySections(
    registryFieldOrder.filter(
      (spec) => !excludedFieldNames.has(spec.name) && nameSetsByType.every((names) => names.has(spec.name)),
    ),
  );

  return eligibleFields.map((spec) => ({
    spec,
    values: dedupeValues(
      items.map((item) => (item as unknown as Record<string, unknown>)[spec.name]),
      spec.kind,
    ),
  }));
};
